import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { config } from '../../environments/environment';
import { User } from '../model/auth/user';
import { SandboxPageStateService } from './sandbox-page-state.service';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class entraAuthService {
  user: User = {} as User;
  graphAccessToken : any; string = '';

  constructor(
    private router: Router,
    private msalService: MsalService,
    private sandboxPageStateService: SandboxPageStateService
  ) {
    this.user = {
      preferredUserName: '',
      userEmail: '',
      isAdminUser: false,
      isSMEUser: false,
      userId: '',
      isNDCAPINewSandboxUser: true
    };
  }

  async initLogin() {
    await this.msalService.loginRedirect({
      scopes: [config.azureAd.apiScope],
      prompt: 'select_account',
      domainHint: '',
      loginHint: ''
    });
    this.getSignedInUser();
  }   

  hasToken(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  getUserRoles(): string[]{
    const account= this.msalService.instance.getActiveAccount();    
    if(!account) return [];
    return (account.idTokenClaims as {roles?: string[]})?.roles || [];
  }
  
  getSignedInUser(): User {
    let account = this.msalService.instance.getActiveAccount();
    if (!account) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        account = accounts[0];
        this.msalService.instance.setActiveAccount(account);
      }
    }

    if (account) {
      this.user.preferredUserName = account.name || '';
      this.user.userEmail = account.username || '';
      this.user.userId = account.localAccountId || '';
      this.user = { preferredUserName: account.name || '', userEmail: account.username || '', userId: account.localAccountId || '' } as User;

      const roles = this.getUserRoles();

      if (roles.includes('SiteAdmin')) {
        this.user.isAdminUser = true;
      }

      if (roles.includes('NDCSandBoxLiveRO')) {
        this.user.isSMEUser = true;
      }

      this.sandboxPageStateService.updateUser(this.user);
    }else {
      this.sandboxPageStateService.resetUser();
      this.user = this.sandboxPageStateService.getUser();
    }
    localStorage.setItem('user_info', JSON.stringify(this.user));
    return this.user;
  }
  
  signOutUser() {
    const account = this.msalService.instance.getActiveAccount();
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: config.azureAd.postLogoutRedirectUri,
      account: account
    });

    this.msalService.instance.clearCache();
    this.sandboxPageStateService.clearLocalStorage(false);
    this.sandboxPageStateService.resetUser();
  }
    
  getTokenObject(): any {
    return this.msalService.instance.getActiveAccount();
  }
  
  getUserDetails(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const account = this.getUserDetails();
      if (!account) return null;

      const result = await firstValueFrom(
        this.msalService.acquireTokenSilent({
          scopes: [config.azureAd.apiScope],
          account: account
        })
      );
      
      return result.accessToken;
    } catch (error) {
      return null;
    }
  }

  private async setTokenToStorage(tokenPromise: Promise<string | null>, key: string, errorMsg: string) {
    try {
      const token = await tokenPromise;
      if (token) {
        localStorage.setItem(key, token);
      } 
    } catch (error) {
      console.log(errorMsg, error);
    }
  }

  async setAccessToken(){
    await this.setTokenToStorage(this.getAccessToken(), 'access_token', 'Access Token Error');
  }

  getUserInfo() {
    const account = this.getUserDetails();
    if (account) {
      return {
        email: account.username,
        name: account.name,
        userId: account.localAccountId
      };
    }
    return null;
  }

  isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  async getGraphAccessToken(): Promise<string | null> {
    try {
      const account = this.getUserDetails();
      if (!account) return null;

      const result = await firstValueFrom(
        this.msalService.acquireTokenSilent({
          scopes: ["User.Read"],
          account: account
        })
      );
      
      return result.accessToken;
    } catch (error) {
      return null;
    }
  }

  async setGraphAccessToken(){
    this.graphAccessToken = await this.getGraphAccessToken();
  }
}
